<template>
  <div class="post-preview-section">
    <ul class="post-preview-section__tabs-row">
      <!-- Tab with logo -->
      <li
        :class="{
          'tab-with-logo': true,
          'tab-with-logo--active': socialNetwork === postPreviewActiveTab,
          'post-preview-section__tab-with-logo': true,
        }"
        v-for="socialNetwork in ['Facebook', 'Instagram']"
        :key="socialNetwork"
        @click="postPreviewActiveTab = socialNetwork"
      >
        <IconEmbedded
          v-if="socialNetwork === 'Facebook'"
          name="facebook"
          :class="{
            'tab-with-logo__logo': true,
            'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
          }"
          color="rgba(59, 89, 152, 1)"
        />
        <IconEmbedded
          v-else-if="socialNetwork === 'Instagram'"
          name="instagram"
          :class="{
            'tab-with-logo__logo': true,
            'tab-with-logo__logo--no-right-margin': ['mobile'].includes(screenSize),
            'tab-with-logo__logo--instagram': true,
          }"
          color="rgba(255, 255, 255, 1)"
        />

        <div v-if="!['mobile'].includes(screenSize)" class="tab-with-logo__text">{{ socialNetwork }}</div>
      </li>
      <!-- / Tab with logo -->
    </ul>

    <div class="post-preview-section__post-snippet-wrap">
      <FbPostPreview v-if="postPreviewActiveTab === 'Facebook'" class="post-preview-section__post-snippet" :images-list="props.assetIds" :fancyboxKey="props.fancyboxKey">
        <template #textarea>
          <textarea
            v-if="props.isWithTextarea"
            class="post-preview-section__post-snippet-textarea"
            :value="props.caption"
            @input="$event => emit('update:caption', ($event.target as HTMLInputElement).value)"
            ref="domRefTextareaFb"
            placeholder="Caption"
          />
          <ClampedText v-else style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="5">
            {{ props.caption }}
          </ClampedText>
        </template>
      </FbPostPreview>

      <InstPostPreview v-else-if="postPreviewActiveTab === 'Instagram'" class="post-preview-section__post-snippet" :images-list="props.assetIds" :fancyboxKey="props.fancyboxKey">
        <template #textarea>
          <textarea
            v-if="props.isWithTextarea"
            class="post-preview-section__post-snippet-textarea"
            :value="props.caption"
            @input="$event => emit('update:caption', ($event.target as HTMLInputElement).value)"
            ref="domRefTextareaInst"
            placeholder="Caption"
          />
          <ClampedText v-else style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="5">
            {{ props.caption }}
          </ClampedText>
        </template>
      </InstPostPreview>

      <!-- Share and dislike buttons -->
      <div v-if="props.isWithShareAndDislikeButtons" class="share-and-dislike-buttons post-preview-section__share-and-dislike-buttons">
        <SrpButton class="share-and-dislike-buttons__share-button" size="small" fill="outlined" @click="emit('clickCustomizeAndShare')">Customize &amp; Share</SrpButton>

        <SrpButton class="share-and-dislike-buttons__dislike-button" size="small" color="gray" fill="outlined" @click="emit('clickDislike')">
          <template #icon><IconEmbedded name="dislike_2" :size="21" /></template>
        </SrpButton>
      </div>
      <!-- / Share and dislike buttons -->

      <!-- What post is based on -->
      <div v-if="props.isWhatPostIsBasedOnVisible && props.post.contextType" class="post-preview-section__what-post-is-based-on">
        Post is based on {{ getCreatedFromPrefix(props.post.contextType) }}
        <IconEmbedded
          style="margin-right: 3px; position: relative; top: 4px"
          :name="getCreatedFromIconName(props.post.contextType)"
          :size="18"
          :color="['customer-upload', 'community-upload'].includes(props.post.contextType) ? 'rgba(0, 0, 0, 0.5)' : 'rgba(12, 135, 138, 1)'"
        />
        <template v-if="['customer-upload', 'community-upload'].includes(props.post.contextType)">
          {{ getCreatedFromFriendlyName(props.post.contextType) }}
        </template>
        <RouterLink v-else :to="getGeneratedFromUrl(props.post)" target="_blank" class="global-a">{{ getCreatedFromFriendlyName(props.post.contextType) }}</RouterLink>
      </div>
      <!-- / What post is based on -->

      <!-- Debug Link -->
      <div v-if="props.showDebug" class="post-preview-section__debug-link-wrap">
        <div v-if="props.post && getCreatedFromFriendlyName(props.post.contextType)" class="post-preview-section__debug-info">
          Based on this <RouterLink :to="getGeneratedFromUrl(props.post)" target="_blank" class="external-link">{{ getCreatedFromFriendlyName(props.post.contextType) }}</RouterLink>
        </div>
        <a v-if="props.debugData" href="#" @click.prevent="debugModalRef?.openModal()" class="post-preview-section__debug-link">Debug</a>
      </div>
      <!-- / Debug Link -->
    </div>
    <!--<TwPostPreview class="post-preview-section__snippet" />-->

    <!-- Debug Modal -->
    <ImageSelectionDebugModal ref="debugModalRef" :debugData="props.debugData" />
  </div>
</template>
<script setup lang="ts">
import { inject, Ref, ref } from "vue";
import { RouteLocationRaw, useRouter, RouterLink } from "vue-router";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import FbPostPreview from "./FbPostPreview.vue";
import InstPostPreview from "./InstPostPreview.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import ClampedText from "@components/ui/ClampedText.vue";
import ImageSelectionDebugModal from "@components/ImageSelectAIDebug/ImageSelectionDebugModal.vue";

// Types
import { ScreenSize } from "@contracts/screenSize";
import { ImageSelectAIDebugData, SocialMediaPostData, SocialPostTypeId } from "@contracts/shareStop";
import { IconName } from "@contracts/IconName";

// Global variables & injections
const screenSize = inject("screenSize") as Ref<ScreenSize>;
const router = useRouter();

const props = withDefaults(
  defineProps<{
    assetIds: Array<string>;
    caption: string;
    isWithTextarea?: boolean;
    isWithShareAndDislikeButtons?: boolean;
    fancyboxKey: string;
    showDebug?: boolean;
    debugData?: ImageSelectAIDebugData | null;
    post?: SocialMediaPostData | null;
    isWhatPostIsBasedOnVisible?: boolean; // ex "Post is based on the Creator Visit"
  }>(),
  {
    assetIds: () => [],
    caption: "",
    isWithTextarea: true,
    isWithShareAndDislikeButtons: false,
    fancyboxKey: "",
    showDebug: false,
    debugData: null,
    post: null,
    isWhatPostIsBasedOnVisible: false,
  }
);

const emit = defineEmits<{
  (e: "clickCustomizeAndShare"): void;
  (e: "update:caption", value: string): void;
  (e: "clickDislike"): void;
}>();

// DOM refs ===================================================================
const domRefTextareaFb = ref<any>(null);
const domRefTextareaInst = ref<any>(null);
const debugModalRef = ref<InstanceType<typeof ImageSelectionDebugModal> | null>(null);

// Switch post preview tabs ===================================================
const postPreviewActiveTab = ref("Facebook");

// Define expose ==============================================================
defineExpose({
  domRefTextareaFb,
  domRefTextareaInst,
});

function getGeneratedFromUrl(post: SocialMediaPostData): RouteLocationRaw {
  switch (post.contextType) {
    case "collabInput":
      return {
        name: "PaidCollabCommunityAdmin",
        params: {
          communityId: post.customerId,
        },
        hash: `#collab=${post.contextId}`,
      };
    case "adventure":
      return {
        name: "ItineraryPreview",
        params: {
          itineraryId: post.contextId,
        },
      };
    case "location":
      return {
        name: "PaidCollabCommunityAdmin",
        params: {
          communityId: post.customerId,
        },
        hash: `#collab=${post.sourceEntityId}`,
      };
    default:
      return "#";
  }
}

function getCreatedFromFriendlyName(contextType: SocialPostTypeId): string | null {
  switch (contextType) {
    case "collabInput":
      return "Creator Visit";
    case "adventure":
      return "Adventure";
    case "location":
      return "Visit Location";
    case "customer-upload":
      return "Your Uploads";
    case "community-upload":
      return "Community Uploads";
    default:
      return null;
  }
}

function getCreatedFromPrefix(contextType: SocialPostTypeId): string {
  switch (contextType) {
    case "collabInput":
      return "a";
    case "adventure":
      return "an";
    case "location":
      return "a";
    default:
      return "";
  }
}

function getCreatedFromIconName(contextType: SocialPostTypeId): IconName {
  switch (contextType) {
    case "collabInput":
      return "briefcase_2";
    case "adventure":
      return "compass_2";
    case "location":
      return "map-pin_2";
    case "customer-upload":
      return "upload_2";
    default:
      return "upload_2";
  }
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Tab with logo ==============================================================
.tab-with-logo {
  height: 42px;
  padding: 0 11px 2px 11px;
  box-sizing: border-box;
  border: 1px #d9d9d9 solid;
  border-width: 1px 1px 0 1px;
  border-radius: 6px 6px 0 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font:
    700 15px/15px "Quicksand",
    sans-serif;
  background: #d9d9d9;
  cursor: pointer;
  user-select: none;

  &--active {
    background: #fff;
  }

  &__logo {
    width: 26px;
    height: 26px;
    margin-right: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 27px;
    line-height: 27px;
    text-align: center;

    &--instagram {
      width: 20px;
      height: 20px;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 18px;
      line-height: 18px;
      background: radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285aeb 90%);
    }

    &--tiktok {
      width: 20px;
      height: 20px;
      padding: 1px 0 0 1px;
      box-sizing: border-box;
      margin-right: 6px;
      border-radius: 5px;
      color: #fff;
      font-size: 12px;
      line-height: 12px;
      background: black;

      &::before {
        filter: drop-shadow(1px 0px 0px #fd3e3e) drop-shadow(-1px -1px 0px #4de8f4);
      }
    }

    &--no-right-margin {
      margin-right: 0;
    }
  }

  &__text {
  }
}

// Share and dislike buttons ==================================================
.share-and-dislike-buttons {
  gap: 12px;
  display: flex;
  justify-content: space-between;

  &__share-button {
  }

  &__dislike-button {
    width: 50px;
  }
}

// Post preview section =======================================================
.post-preview-section {
  display: flex;
  flex-direction: column;

  &__tabs-row {
    padding: 0;
    margin: 0 0 -1px;
    display: flex;
    list-style: none;
  }

  &__tab-with-logo {
    margin-right: 5px;
  }

  &__post-snippet-wrap {
    flex-grow: 1;
    padding: 12px 3px 13px;
    border: 1px #d7d7d7 solid;
    border-radius: 0 6px 6px 6px;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  &__post-snippet {
    margin-bottom: 19px;
  }

  &__post-snippet-textarea {
    scroll-behavior: smooth;
  }

  &__share-and-dislike-buttons {
    margin: 0 13px 17px;
  }

  &__what-post-is-based-on {
    @include standardFont;
    padding: 11px 0 0;
    margin: 0 12px;
    border-top: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__debug-link-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 10px 13px 0;
  }

  &__debug-info {
    font-size: 12px;
    color: #666;

    .external-link {
      color: #007bff;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__debug-link {
    font-size: 12px;
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
